'use client'
import { useState, useCallback } from 'react'

export const storageUtilFactory = (storage: Storage) => {
  const setItem = <T>(key: string, data: T) => {
    storage.setItem(key, JSON.stringify(data))
  }

  const getItem = <TData>(
    key: string,
    defaultValue: TData | null = null,
  ): TData | null => {
    const value = storage.getItem(key)
    return value ? JSON.parse(value) : defaultValue
  }

  const removeItem = (key: string) => storage.removeItem(key)
  const clear = () => storage.clear()

  return {
    setItem,
    getItem,
    removeItem,
    clear,
  }
}

// SSR-safe: only create storage services on the client side
export const localStorageService =
  typeof window !== 'undefined'
    ? storageUtilFactory(window.localStorage)
    : storageUtilFactory({
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        key: () => null,
        length: 0,
      } as Storage)

export const sessionStorageService =
  typeof window !== 'undefined'
    ? storageUtilFactory(window.sessionStorage)
    : storageUtilFactory({
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        key: () => null,
        length: 0,
      } as Storage)

const createUseStorageFactory =
  (storage: ReturnType<typeof storageUtilFactory>) =>
  <T>(key: string, initialValue?: T) => {
    const [value, setStoredValue] = useState(() => {
      const item = storage.getItem(key, initialValue)
      return item
    })
    const setValue = useCallback(
      (newValue: T): void => {
        setStoredValue(newValue)
        storage.setItem(key, newValue)
      },
      [setStoredValue, key],
    )

    return [value, setValue] as [T, (value: T) => void]
  }

export const useLocalStorage = createUseStorageFactory(localStorageService)
export const useSessionStorage = createUseStorageFactory(sessionStorageService)
